/**
 * useFileVideoUpload — upload a local match-video file to S3 in chunks so the
 * existing per-ball MediaConvert slicer can clip it.
 *
 * Pairs with the "Upload from computer" mode on VideoPlayer. The user picks a
 * file, scrubs through it, marks clip in/out per ball (those offsets save to
 * each Ball row immediately), and clicks "● Upload for clipping". This hook
 * then:
 *
 *   1. Creates a MatchRecording (tier=B2C) on the server, getting an upload_id.
 *   2. Slices the File blob into adaptive-size chunks (5–16 MB) and PUTs each
 *      one to S3 via a presigned URL.
 *   3. Persists in-flight chunks to IDB before upload so a tab crash / network
 *      drop is fully recoverable — on next mount we detect the orphan and the
 *      caller can resume.
 *   4. On the final chunk, calls completeRecording which fires the slicer that
 *      cuts every Ball with video_start_ms/end_ms into a per-ball mp4 in S3.
 *
 * The clip-in/out timestamps work BEFORE this hook runs — the user can score
 * the whole match against a local file, then click upload at the end. The
 * offsets save server-side per-ball; the upload just unblocks the slicer.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  startRecording, presignPart, completeRecording, abortRecording,
} from "../api/recordings.api";

const IDB_DB = "zcd_file_upload_v1";
const IDB_STORE = "uploads";

// S3 multipart requires every part EXCEPT the last to be ≥ 5 MB. Use 5 MB as
// the floor — anything smaller and complete_multipart_upload returns
// EntityTooSmall and the upload is unrecoverable.
const CHUNK_MIN = 5 * 1024 * 1024;   // 5 MB — hard S3 minimum
const CHUNK_MAX = 16 * 1024 * 1024;  // 16 MB — adaptive ceiling
const CHUNK_TARGET_SECONDS = 3;      // each chunk should take ~3s — fast enough to feel snappy, big enough to amortize HTTP overhead

function idbOpen() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(IDB_DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(IDB_STORE, { keyPath: "match_id" });
    r.onsuccess = () => res(r.result);
    r.onerror = () => rej(r.error);
  });
}
async function idbPut(matchId, state) {
  const db = await idbOpen();
  const tx = db.transaction(IDB_STORE, "readwrite");
  tx.objectStore(IDB_STORE).put({ match_id: matchId, ...state, ts: Date.now() });
  return new Promise(r => tx.oncomplete = r);
}
async function idbGet(matchId) {
  const db = await idbOpen();
  return new Promise(res => {
    const tx = db.transaction(IDB_STORE, "readonly");
    const req = tx.objectStore(IDB_STORE).get(matchId);
    req.onsuccess = () => res(req.result || null);
    req.onerror = () => res(null);
  });
}
async function idbDelete(matchId) {
  const db = await idbOpen();
  const tx = db.transaction(IDB_STORE, "readwrite");
  tx.objectStore(IDB_STORE).delete(matchId);
  return new Promise(r => tx.oncomplete = r);
}

export function useFileVideoUpload({ matchId }) {
  const fileRef = useRef(null);              // File blob the user picked
  const [fileLoaded, setFileLoaded] = useState(false);
  const [fileName, setFileName] = useState("");
  const [fileTimeMs, setFileTimeMs] = useState(0);

  const [state, setState] = useState("idle"); // idle | preparing | uploading | complete | error
  const [error, setError] = useState("");
  const [recordingId, setRecordingId] = useState(null);
  const [bytesUploaded, setBytesUploaded] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const cancelRef = useRef(false);

  // Detect orphan upload for this match on mount — caller can show a "Resume"
  // banner. The orphan record carries recording_id + completed parts so we can
  // skip what S3 already has.
  const [orphan, setOrphan] = useState(null);
  useEffect(() => {
    if (!matchId) return;
    idbGet(matchId).then(o => {
      if (o && o.completed_parts && !o.finalized) setOrphan(o);
    });
  }, [matchId]);

  // VideoPlayer fires onFileLoad(file) when the user picks a file.
  const registerFile = useCallback((file) => {
    fileRef.current = file;
    setFileLoaded(!!file);
    setFileName(file?.name || "");
    setFileTimeMs(0);
    setState("idle");
    setError("");
    setBytesUploaded(0);
    setTotalBytes(file?.size || 0);
  }, []);

  const updateTime = useCallback((ms) => {
    setFileTimeMs(Math.max(0, Math.round(ms)));
  }, []);

  // The actual upload. Idempotent — safe to call again after a failure; uses
  // IDB to resume from the next un-uploaded part.
  const uploadFile = useCallback(async () => {
    const file = fileRef.current;
    if (!file) { setError("No file loaded"); return; }
    if (state === "uploading" || state === "preparing") return;

    cancelRef.current = false;
    setState("preparing");
    setError("");

    try {
      // Either resume an existing recording or start a fresh one.
      let recId, uploadId;
      let completed = []; // [{PartNumber, ETag}]
      let nextPart = 1;

      const existing = await idbGet(matchId);
      if (existing && existing.recording_id && existing.file_size === file.size && !existing.finalized) {
        recId = existing.recording_id;
        uploadId = existing.upload_id;
        completed = existing.completed_parts || [];
        nextPart = (completed.length ? Math.max(...completed.map(p => p.PartNumber)) : 0) + 1;
      } else {
        const startRes = await startRecording(matchId, "B2C");
        recId = startRes.recording_id;
        uploadId = startRes.upload_id;
        await idbPut(matchId, {
          recording_id: recId,
          upload_id: uploadId,
          file_size: file.size,
          file_name: file.name,
          completed_parts: [],
          finalized: false,
        });
      }
      setRecordingId(recId);
      setState("uploading");

      // Resume cursor — bytes already uploaded. Assume CHUNK_MAX per part so
      // the progress bar is pessimistic; it catches up on the next real chunk.
      let byteCursor = Math.min(completed.length * CHUNK_MAX, file.size);
      setBytesUploaded(byteCursor);

      // Adaptive chunk size — start small, grow up to CHUNK_MAX based on
      // measured upload speed.
      let chunkSize = CHUNK_MIN;

      while (byteCursor < file.size) {
        if (cancelRef.current) break;
        const end = Math.min(byteCursor + chunkSize, file.size);
        const blob = file.slice(byteCursor, end);

        const t0 = performance.now();
        const { url } = await presignPart(recId, nextPart);
        const res = await fetch(url, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": "application/octet-stream" },
        });
        if (!res.ok) throw new Error(`S3 PUT failed: ${res.status}`);
        const etag = res.headers.get("ETag")?.replaceAll('"', "") || "";
        const elapsedMs = performance.now() - t0;

        completed.push({ PartNumber: nextPart, ETag: etag });
        byteCursor = end;
        nextPart += 1;
        setBytesUploaded(byteCursor);

        // Persist after each chunk so a crash leaves a recoverable trail.
        await idbPut(matchId, {
          recording_id: recId,
          upload_id: uploadId,
          file_size: file.size,
          file_name: file.name,
          completed_parts: completed,
          finalized: false,
        });

        // Tune chunk size for next iteration. Aim for CHUNK_TARGET_SECONDS
        // per chunk. If the last one was much faster, grow; much slower, shrink.
        if (elapsedMs > 0) {
          const measuredBps = (blob.size / elapsedMs) * 1000;
          const target = Math.round(measuredBps * CHUNK_TARGET_SECONDS);
          chunkSize = Math.max(CHUNK_MIN, Math.min(CHUNK_MAX, target));
        }
      }

      if (cancelRef.current) {
        setState("idle");
        return;
      }

      // Finalize on the server — triggers the slicer.
      await completeRecording(recId, {
        parts: completed.sort((a, b) => a.PartNumber - b.PartNumber),
        duration_ms: 0, // unknown for file uploads; backend tolerates 0
        byte_size: file.size,
      });
      await idbPut(matchId, {
        recording_id: recId,
        upload_id: uploadId,
        file_size: file.size,
        file_name: file.name,
        completed_parts: completed,
        finalized: true,
      });
      // Remove orphan flag now that we're done.
      await idbDelete(matchId);
      setOrphan(null);
      setState("complete");
    } catch (err) {
      setError(err?.message || "Upload failed");
      setState("error");
    }
  }, [matchId, state]);

  const cancelUpload = useCallback(async () => {
    cancelRef.current = true;
    if (recordingId) {
      try { await abortRecording(recordingId); } catch { /* server may not know about it */ }
    }
    await idbDelete(matchId);
    setOrphan(null);
    setBytesUploaded(0);
    setState("idle");
    setError("");
  }, [matchId, recordingId]);

  const resumeUpload = useCallback(() => {
    if (!orphan) return;
    setOrphan(null);
    uploadFile();
  }, [orphan, uploadFile]);

  const isLive = fileLoaded; // file mode supports clip in/out as soon as a file is loaded

  return {
    state,
    error,
    fileLoaded,
    fileName,
    fileTimeMs,
    bytesUploaded,
    totalBytes,
    recordingId,
    orphan,
    isLive,
    registerFile,
    updateTime,
    uploadFile,
    cancelUpload,
    resumeUpload,
  };
}
